import styled, { keyframes } from "styled-components";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { router } from "@inertiajs/react";
import {
  X,
  ExternalLink,
  LogIn,
  LogOut,
  Wallet,
  Info,
  Globe,
  Banknote,
  BookOpen,
  RotateCw,
  RefreshCw,
  QrCode,
  MessageCircle,
  Mail,
  Pencil,
  AlertTriangle,
  ArrowLeftRight,
  FileText,
  Download,
  Zap,
  Check,
  Plus,
  Trash2,
  BedDouble,
  Clock,
  CheckCircle2,
} from "lucide-react";

import { theme } from "./theme";
import StatusBadge from "./StatusBadge";
import RoomMoveModal from "./RoomMoveModal";
import PhoneField, { WA_COUNTRIES, detectWaCountry } from "./PhoneField";
import { physicalStatusMeta } from "./status";
import { apiFetch } from "./api";
import { formatDate, formatDateTime, wibTodayIso } from "./dateFormat";
import { FoNotesButton, GuestNotesButton } from "./NotesButtons";
import DepositGuideModal from "./DepositGuideModal";
import { cleanComment } from "./text";
import { useT } from "./i18n";

// The FO's single workspace for one booking: summary + guest + rooms + payment + deposit + refund,
// headlined by ONE state-derived primary action ("what should I do next?"). Loads asynchronously with
// a skeleton; money actions arrive in the next increment (until then: "Buka di /admin").
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1400;
  background: rgba(26, 26, 26, 0.45);
  backdrop-filter: blur(2px);
`;

const slideIn = keyframes`
  from { transform: translateX(40px); opacity: 0.4; }
  to   { transform: translateX(0); opacity: 1; }
`;

const Panel = styled.aside`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 1401;
  width: min(680px, 100vw);
  display: flex;
  flex-direction: column;
  background: ${theme.canvas};
  border-left: 3px solid ${theme.gold};
  box-shadow: -18px 0 50px -20px rgba(26, 26, 26, 0.55);
  animation: ${slideIn} 0.22s ease;
`;

const Head = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 1rem 1.25rem 0.75rem;
  background: #ffffff;
  border-bottom: 1px solid ${theme.border};
`;

const HeadGuest = styled.div`
  font-size: 1.15rem;
  font-weight: 800;
  color: ${theme.ink};
`;

// A strict two-column label/value grid so every field sits in a fixed spot the eye can memorise.
const HeadMeta = styled.div`
  margin-top: 0.4rem;
  display: grid;
  grid-template-columns: auto 1fr;
  row-gap: 0.35rem;
  column-gap: 0.7rem;
  align-items: center;
  font-size: 1rem;
`;

const MetaLabel = styled.span`
  font-size: 1rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: ${theme.muted};
`;

const MetaValue = styled.span`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  min-width: 0;
  font-size: 1rem;
  font-weight: 600;
  color: ${theme.ink700};
  word-break: break-word;
`;

const HeadActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
`;

const AdminLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: ${theme.goldDark};
  text-decoration: none;
  white-space: nowrap;
  &:hover {
    text-decoration: underline;
  }
`;

const CloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: 1px solid ${theme.border};
  border-radius: 8px;
  background: #ffffff;
  cursor: pointer;
  &:hover {
    background: ${theme.goldSoft};
  }
`;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

// Pulsing gold glow for the "don't forget me" deposit buttons while the deposit is still unpaid.
const glow = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(201, 168, 108, 0.65), 0 0 6px 0 rgba(201, 168, 108, 0.5); }
  70% { box-shadow: 0 0 0 8px rgba(201, 168, 108, 0), 0 0 14px 3px rgba(201, 168, 108, 0.55); }
  100% { box-shadow: 0 0 0 0 rgba(201, 168, 108, 0), 0 0 6px 0 rgba(201, 168, 108, 0.5); }
`;

const RefreshButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: 1px solid ${theme.border};
  border-radius: 8px;
  background: #ffffff;
  color: ${theme.goldDark};
  cursor: pointer;
  &:hover {
    background: ${theme.goldSoft};
  }
  &[data-spin="true"] svg {
    animation: ${spin} 0.8s linear infinite;
  }
`;

const ChipButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.32rem 0.62rem;
  border: 1.5px solid ${theme.goldDark};
  border-radius: 8px;
  background: ${theme.goldSoft};
  color: ${theme.goldDark};
  font-size: 0.82rem;
  font-weight: 800;
  line-height: 1;
  cursor: pointer;
  vertical-align: middle;
  transition:
    background 0.12s,
    color 0.12s,
    border-color 0.12s;
  &:hover {
    background: ${theme.goldDark};
    color: #ffffff;
    border-color: ${theme.goldDark};
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

// Hover tooltip: wrap a control in <TipWrap> and add <Tip className="fdtip">…</Tip> as a sibling of
// the control so it reveals on hover. Used to explain the compact QR / WA / edit chips.
const TipWrap = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  &:hover .fdtip,
  &:focus-within .fdtip {
    opacity: 1;
    visibility: visible;
    transform: translate(-50%, 0);
  }
`;

const Tip = styled.span`
  position: absolute;
  top: calc(100% + 7px);
  left: 50%;
  transform: translate(-50%, -4px);
  width: max-content;
  max-width: 230px;
  padding: 0.4rem 0.6rem;
  border-radius: 7px;
  background: ${theme.ink};
  color: #ffffff;
  font-size: 0.74rem;
  font-weight: 600;
  line-height: 1.35;
  text-align: center;
  white-space: normal;
  opacity: 0;
  visibility: hidden;
  transition:
    opacity 0.12s,
    transform 0.12s;
  z-index: 1600;
  pointer-events: none;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.2);
  &::after {
    content: "";
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-bottom-color: ${theme.ink};
  }
`;

const QrBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1500;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(26, 26, 26, 0.72);
  padding: 1.5rem;
`;

const QrCard = styled.div`
  background: #ffffff;
  border-radius: 14px;
  padding: 1.5rem;
  text-align: center;
  max-width: 90vw;
`;

const QrLargeImg = styled.img`
  width: 360px;
  max-width: 78vw;
  height: auto;
  display: block;
  margin: 0 auto;
`;

const QrCodeText = styled.div`
  margin-top: 0.9rem;
  font-family: monospace;
  font-size: 1.35rem;
  font-weight: 700;
  color: ${theme.ink};
  letter-spacing: 0.06em;
`;

const QrHint = styled.div`
  margin-top: 0.4rem;
  font-size: 0.78rem;
  color: ${theme.muted};
`;

const QrCloseButton = styled.button`
  margin-top: 1.1rem;
  padding: 0.4rem 1.4rem;
  border: 1px solid ${theme.border};
  border-radius: 8px;
  background: ${theme.canvas};
  color: ${theme.ink};
  font-weight: 600;
  cursor: pointer;
`;

const StatusChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.1rem 0.5rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  color: ${(props) => props.$fg};
  background: ${(props) => props.$bg};
`;

const Body = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 1rem 1.25rem 2rem;
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
`;

const PrimaryCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
  border-radius: 12px;
  background: ${(props) => props.$bg || "#ffffff"};
  border: 1px solid ${(props) => props.$border || theme.border};
`;

const PrimaryText = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: ${theme.ink};
`;

const PrimaryHint = styled.div`
  margin-top: 0.15rem;
  font-size: 1rem;
  font-weight: 400;
  color: ${theme.ink700};
`;

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.55rem 1rem;
  border: 0;
  border-radius: 9px;
  background: ${theme.ink};
  color: #ffffff;
  font-size: 0.88rem;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  &:hover {
    background: ${theme.inkSoft};
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// "Deposit paid, guest not checked in yet" — the QRH next-step nudge for the FO.
const NextStepCard = styled.div`
  padding: 0.9rem 1rem;
  border-radius: 12px;
  background: #eaf7ef;
  border: 1px solid #b7e0c6;
`;

const NextStepTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 1rem;
  font-weight: 800;
  color: #166534;
`;

const NextStepIntro = styled.div`
  margin-top: 0.2rem;
  font-size: 0.95rem;
  color: ${theme.ink700};
`;

const NextStepList = styled.ol`
  margin: 0.65rem 0 0;
  padding: 0;
  list-style: none;
  counter-reset: ns;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
`;

const NextStepItem = styled.li`
  counter-increment: ns;
  position: relative;
  padding-left: 1.95rem;
  font-size: 0.95rem;
  line-height: 1.4;
  color: ${theme.ink};
  &::before {
    content: counter(ns);
    position: absolute;
    left: 0;
    top: 0;
    width: 1.45rem;
    height: 1.45rem;
    border-radius: 50%;
    background: #166534;
    color: #ffffff;
    font-size: 0.8rem;
    font-weight: 800;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const NextStepNote = styled.div`
  margin-top: 0.65rem;
  padding: 0.5rem 0.7rem;
  border-radius: 8px;
  background: ${theme.goldSoft};
  border-left: 4px solid ${theme.goldDark};
  font-size: 0.9rem;
  font-weight: 700;
  color: ${theme.ink};
`;

const Section = styled.section`
  background: #ffffff;
  border: 1px solid ${theme.border};
  border-radius: 12px;
  padding: 0.85rem 1rem;
`;

const SectionTitle = styled.h3`
  margin: 0 0 0.6rem;
  font-size: 1rem;
  font-weight: 700;
  color: ${theme.muted};
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const KV = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.25rem 0;
  font-size: 1rem;
  color: ${theme.ink700};
  strong {
    color: ${theme.ink};
    text-align: right;
  }
`;

const LineCard = styled.div`
  border: 1px solid ${theme.border};
  border-left: 3px solid ${(props) => props.$accent || theme.border};
  border-radius: 10px;
  padding: 0.6rem 0.7rem;
  margin-bottom: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  background: ${(props) => (props.$anchor ? theme.goldSoft : "#ffffff")};
`;

const LineHead = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const LineRoom = styled.span`
  font-size: 1.1rem;
  font-weight: 800;
  color: ${theme.ink};
`;

const LineType = styled.span`
  font-size: 1rem;
  color: ${theme.muted};
  flex: 1 1 auto;
`;

const LineDates = styled.div`
  font-size: 1rem;
  color: ${theme.ink700};
`;

const LineDeposit = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.95rem;
  color: ${theme.ink700};
`;

const SmallButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: ${(props) => (props.$emph ? "0.5rem 1rem" : "0.4rem 0.8rem")};
  border: 0;
  border-radius: 8px;
  background: ${(props) => (props.$emph ? theme.goldDark : theme.ink)};
  color: #ffffff;
  font-size: 1rem;
  font-weight: ${(props) => (props.$emph ? 800 : 700)};
  cursor: pointer;
  animation: ${(props) => (props.$emph ? glow : "none")} 1.6s ease-in-out
    infinite;
  &:hover {
    background: ${(props) => (props.$emph ? "#74592f" : theme.inkSoft)};
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    animation: none;
  }
`;

const Blockers = styled.div`
  font-size: 1rem;
  color: #991b1b;
  line-height: 1.4;
`;

const ActionBar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.6rem;
  padding-top: 0.6rem;
  border-top: 1px dashed ${theme.border};
  font-size: 0.78rem;
  color: ${theme.ink700};
`;

const MethodSelect = styled.select`
  padding: 0.28rem 0.4rem;
  border: 1px solid ${theme.border};
  border-radius: 8px;
  font-size: 0.78rem;
  background: #ffffff;
`;

const VoucherInput = styled.input`
  padding: 0.28rem 0.5rem;
  border: 1px solid ${theme.border};
  border-radius: 8px;
  font-size: 0.78rem;
  background: #ffffff;
  width: 9rem;
  text-transform: uppercase;

  &::placeholder {
    text-transform: none;
  }
`;

// The voucher sits on its own row (apply → validate → discount line), separate from the payment
// buttons so the FO can clearly see whether a code is valid before charging.
const VoucherRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.5rem 0;
  border-top: 1px dashed ${theme.border};
`;

const VoucherLabel = styled.span`
  font-size: 0.78rem;
  font-weight: 800;
  color: ${theme.ink};
  margin-right: 0.15rem;
`;

const VoucherOk = styled.span`
  font-size: 0.76rem;
  font-weight: 800;
  color: #15803d;
`;

const VoucherErr = styled.span`
  font-size: 0.76rem;
  font-weight: 700;
  color: #b91c1c;
`;

// A clear, large-hit-target "include deposit" checkbox (the bare <input> was tiny and easy to miss).
// The whole pill toggles; the box + tint show the state at a glance.
const DepositCheck = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.42rem 0.75rem;
  border: 1.5px solid ${(props) => (props.$on ? theme.gold : theme.border)};
  border-radius: 10px;
  background: ${(props) => (props.$on ? theme.goldSoft : "#ffffff")};
  color: ${theme.ink};
  font-size: 0.82rem;
  font-weight: 800;
  cursor: pointer;
  &:hover {
    border-color: ${theme.gold};
  }
`;

const DepositBox = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 6px;
  border: 1.5px solid ${(props) => (props.$on ? theme.goldDark : theme.border)};
  background: ${(props) => (props.$on ? theme.gold : "#ffffff")};
  color: #ffffff;
`;

// The deposit line in the pay breakdown strikes through + greys when the FO unchecks "include deposit",
// so the Total (which drops the deposit) is visibly explained.
const DepositAmount = styled.strong`
  color: ${(props) => (props.$off ? theme.muted : "inherit")};
  text-decoration: ${(props) => (props.$off ? "line-through" : "none")};
`;

// Per-line add-ons (extra bed + early check-in): a dashed sub-block inside the room card.
const AddOnBlock = styled.div`
  margin-top: 0.35rem;
  padding-top: 0.4rem;
  border-top: 1px dashed ${theme.border};
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const AddOnLine = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.4rem;
  font-size: 0.92rem;
  color: ${theme.ink700};
`;

const AddOnLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  flex: 1 1 auto;
  font-weight: 800;
  color: ${theme.ink};
`;

const AddOnPrice = styled.span`
  font-weight: 700;
  color: ${theme.ink700};
`;

const AddOnNote = styled.div`
  font-size: 0.82rem;
  color: ${theme.muted};
  font-style: italic;
`;

// Itemized "what will be charged" panel in the deposit section (deposit + selected add-ons + total),
// so the FO sees the exact payable amount before collecting — avoids mis-charging.
const SummaryBox = styled.div`
  margin-top: 0.5rem;
  border: 1px solid ${theme.gold};
  border-radius: 10px;
  padding: 0.6rem 0.7rem;
  background: ${theme.goldSoft};
`;

const SummaryHead = styled.div`
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-weight: 800;
  font-size: 0.92rem;
  color: ${theme.ink};
  margin-bottom: 0.4rem;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 0.5rem;
  padding: 0.12rem 0;
  font-size: ${(props) => (props.$total ? "1rem" : "0.9rem")};
  font-weight: ${(props) => (props.$total ? 800 : 500)};
  color: ${(props) =>
    props.$muted ? theme.muted : props.$total ? theme.ink : theme.ink700};
`;

const SummaryDivider = styled.div`
  border-top: 1px dashed ${theme.gold};
  margin: 0.35rem 0;
`;

const TinyButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.55rem;
  border: 1px solid ${(props) => (props.$danger ? "#f2c4c0" : theme.border)};
  border-radius: 7px;
  background: #ffffff;
  color: ${(props) => (props.$danger ? "#991b1b" : theme.ink)};
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
  &:hover {
    border-color: ${(props) => (props.$danger ? "#991b1b" : theme.gold)};
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// At-a-glance "who processes this payment": Xendit (online) gets a branded indigo pill + bolt; anything
// else (cash/EDC/QRIS/transfer at the desk) is a neutral "Manual" pill.
const ProviderChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.12rem 0.5rem;
  border-radius: 6px;
  font-size: 0.72rem;
  font-weight: 800;
  background: ${(props) => (props.$xendit ? "#e8ecff" : "#eef1f5")};
  color: ${(props) => (props.$xendit ? "#3538cd" : theme.muted)};
  border: 1px solid ${(props) => (props.$xendit ? "#c7cffb" : theme.border)};
`;

const WaSendButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.28rem 0.65rem;
  border: 0;
  border-radius: 8px;
  background: #25d366;
  color: #ffffff;
  font-size: 0.8rem;
  font-weight: 800;
  cursor: pointer;
  &:hover {
    background: #1ebe5b;
  }
`;

// Normalise an Indonesian phone to wa.me digits (leading 0 → 62, strip non-digits).
const waNumber = (raw) => {
  const digits = String(raw || "").replace(/\D/g, "");
  if (!digits) {
    return "";
  }
  return digits.startsWith("0") ? `62${digits.slice(1)}` : digits;
};

const SubSection = styled.div`
  margin-top: 0.6rem;
  padding-top: 0.6rem;
  border-top: 1px dashed ${theme.border};
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
`;

const SubHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`;

const SubTitle = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.72rem;
  font-weight: 800;
  color: ${theme.goldDark};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const SubActions = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.4rem;
  font-size: 0.78rem;
  color: ${theme.ink700};
`;

const GuideLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.25rem 0.55rem;
  border: 1px solid ${theme.border};
  border-radius: 8px;
  background: #ffffff;
  color: ${theme.goldDark};
  font-size: 0.74rem;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  &:hover {
    background: ${theme.goldSoft};
  }
`;

const ActionMsg = styled.div`
  padding: 0.55rem 0.75rem;
  border-radius: 10px;
  font-size: 0.82rem;
  font-weight: 600;
  color: ${(props) => (props.$ok ? "#166534" : "#991b1b")};
  background: ${(props) => (props.$ok ? "#e8f6ee" : "#fdecec")};
  border: 1px solid ${(props) => (props.$ok ? "#b7e0c6" : "#f2c4c0")};
`;

const NotesRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.3rem;
`;

// Change / Swap room buttons sit on the far right of the notes row, only for still-reserved lines.
const RoomMoveButtons = styled.div`
  margin-left: auto;
  display: inline-flex;
  gap: 0.3rem;
`;

const ChangeRoomButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.28rem 0.6rem;
  border: 1px solid ${theme.goldDark};
  border-radius: 8px;
  background: ${theme.goldSoft};
  color: ${theme.goldDark};
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: ${theme.goldDark};
    color: #ffffff;
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const OtaBox = styled.div`
  padding: 0.4rem 0.55rem;
  border: 1px solid #f0dfc0;
  border-radius: 8px;
  background: #fff8ec;
  font-size: 1rem;
  color: ${theme.ink700};
  white-space: pre-wrap;
  word-break: break-word;
`;

const InfoNote = styled.div`
  display: flex;
  gap: 0.4rem;
  align-items: flex-start;
  padding: 0.5rem 0.65rem;
  border-radius: 8px;
  background: ${theme.canvas};
  border: 1px solid ${theme.border};
  font-size: 1rem;
  color: ${theme.ink700};
`;

const shimmer = keyframes`
  0% { opacity: 0.55; }
  50% { opacity: 1; }
  100% { opacity: 0.55; }
`;

const SkeletonBlock = styled.div`
  height: ${(props) => props.$h || "72px"};
  border-radius: 12px;
  background: #eceae4;
  animation: ${shimmer} 1.2s ease-in-out infinite;
`;

const ErrorBox = styled.div`
  padding: 1.5rem;
  text-align: center;
  color: ${theme.ink700};
`;

const RetryButton = styled.button`
  margin-top: 0.75rem;
  padding: 0.45rem 1rem;
  border: 1px solid ${theme.border};
  border-radius: 8px;
  background: #ffffff;
  font-weight: 700;
  cursor: pointer;
`;

const LINE_STATUS_META = {
  reserved: { key: "line_reserved", bg: "#fdf0e1", fg: "#b45309" },
  checked_in: { key: "line_checked_in", bg: "#e7f0fb", fg: "#1d5fa8" },
  checked_out: { key: "line_checked_out", bg: "#eef1f5", fg: "#334155" },
  canceled: { key: "line_canceled", bg: "#fdecec", fg: "#c0392b" },
};

// Per-room deposit-refund state → human label + colour, so the FO reads exactly where each room is in
// the check-out → HK-secure → refund progression (see RoomBookingLine#refund_overview_state).
const REFUND_STATE_META = {
  not_checked_out: { key: "rfs_not_checked_out", bg: "#eef1f5", fg: "#475569" },
  awaiting_hk: { key: "rfs_awaiting_hk", bg: "#fef3c7", fg: "#92400e" },
  review_cash: { key: "rfs_review_cash", bg: "#fee2e2", fg: "#991b1b" },
  held: { key: "rfs_held", bg: "#fef3c7", fg: "#92400e" },
  refundable: { key: "rfs_refundable", bg: "#dcfce7", fg: "#166534" },
  refundable_cash: { key: "rfs_refundable_cash", bg: "#dcfce7", fg: "#166534" },
  claimed: { key: "rfs_claimed", bg: "#dbeafe", fg: "#1e40af" },
  refunded: { key: "rfs_refunded", bg: "#dcfce7", fg: "#166534" },
  refunded_cash: { key: "rfs_refunded", bg: "#dcfce7", fg: "#166534" },
  kept: { key: "rfs_kept", bg: "#eef1f5", fg: "#475569" },
  not_eligible: { key: "rfs_not_eligible", bg: "#eef1f5", fg: "#475569" },
};

// Online (Xendit) refund payout status → colour. Neutral while pending, gold once the link is out,
// blue when the guest has claimed it, green on completion, red/grey for the dead states.
const ONLINE_REFUND_META = {
  pending: { bg: "#eef1f5", fg: "#475569" },
  payout_created: { bg: "#e0f2fe", fg: "#075985" },
  whatsapp_sent: { bg: "#f5eee0", fg: "#8c6d3f" },
  token_verified: { bg: "#dbeafe", fg: "#1e40af" },
  completed: { bg: "#dcfce7", fg: "#166534" },
  failed: { bg: "#fee2e2", fg: "#991b1b" },
  expired: { bg: "#f1f5f9", fg: "#64748b" },
  canceled: { bg: "#f1f5f9", fg: "#64748b" },
};

// Payment / deposit status → colour + label key, so a status reads as a coloured badge (not plain
// text). The status VALUE is unchanged; this only differentiates it visually.
const PAYMENT_STATUS_META = {
  paid: { key: "pay_paid", bg: "#dcfce7", fg: "#166534" },
  pending: { key: "pay_pending", bg: "#fef3c7", fg: "#92400e" },
  pending_payment: { key: "pay_pending", bg: "#fef3c7", fg: "#92400e" },
  expired: { key: "pay_expired", bg: "#f1f5f9", fg: "#475569" },
  failed: { key: "pay_failed", bg: "#fee2e2", fg: "#991b1b" },
  canceled: { key: "pay_canceled", bg: "#f1f5f9", fg: "#475569" },
  unpaid: { key: "pay_unpaid", bg: "#f1f5f9", fg: "#475569" },
};

const paymentStatusMeta = (status) =>
  PAYMENT_STATUS_META[status] || {
    key: null,
    bg: "#f1f5f9",
    fg: "#475569",
  };

const PayBadge = (props) => {
  const { t } = useT();
  const meta = paymentStatusMeta(props.status);
  return (
    <StatusChip $bg={meta.bg} $fg={meta.fg}>
      {meta.key ? t(meta.key) : String(props.status || "").replace(/_/g, " ")}
    </StatusChip>
  );
};

const PayItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid ${theme.border};
  &:last-child {
    border-bottom: 0;
  }
`;

const PayItemTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: ${theme.ink700};
`;

const PayMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
`;

const PayItemBottom = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.55rem;
  font-size: 0.86rem;
`;

const PayInvoice = styled.span`
  font-size: 0.78rem;
  color: ${theme.muted};
  font-variant-numeric: tabular-nums;
`;

const PdfLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.8rem;
  font-weight: 700;
  color: ${theme.goldDark};
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

// WhatsApp country codes for the send-voucher modal (mirrors the /admin dial-code picker).
const WaCard = styled.div`
  background: #ffffff;
  border-radius: 14px;
  padding: 1.5rem;
  width: min(420px, 92vw);
`;

const WaTitle = styled.h3`
  margin: 0 0 0.5rem;
  font-size: 1.1rem;
  font-weight: 800;
  color: ${theme.ink};
`;

const WaDesc = styled.p`
  margin: 0 0 1rem;
  font-size: 0.95rem;
  color: ${theme.ink700};
`;

const WaRow = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const WaSelect = styled.select`
  flex: 0 0 auto;
  max-width: 170px;
  padding: 0.6rem 0.5rem;
  border: 1px solid ${theme.border};
  border-radius: 8px;
  font-size: 1rem;
  background: #ffffff;
`;

const WaInput = styled.input`
  flex: 1 1 auto;
  min-width: 0;
  padding: 0.6rem 0.7rem;
  border: 1px solid ${theme.border};
  border-radius: 8px;
  font-size: 1rem;
`;

const WaHint = styled.div`
  margin-top: 0.4rem;
  font-size: 0.85rem;
  color: ${theme.muted};
`;

const WaErr = styled.div`
  margin-top: 0.5rem;
  font-size: 0.9rem;
  font-weight: 600;
  color: #b91c1c;
`;

const WaActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.6rem;
  margin-top: 1.25rem;
`;

const WaCancel = styled.button`
  padding: 0.55rem 1rem;
  border: 1px solid ${theme.border};
  border-radius: 8px;
  background: ${theme.canvas};
  color: ${theme.ink};
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
`;

const WaSend = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.55rem 1.1rem;
  border: 0;
  border-radius: 8px;
  background: #1f9d55;
  color: #ffffff;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  &:hover {
    background: #178a49;
  }
`;

// In-app confirmation dialog (replaces window.confirm) for the heavier drawer actions — a title, a
// plain-language explanation, and an explicit Ya / Batal choice. Reuses the WaCard shell + backdrop.
const ConfirmBody = styled.p`
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.55;
  color: ${theme.ink700};
`;

const ConfirmNote = styled.p`
  margin: 0.85rem 0 0;
  padding: 0.7rem 0.85rem;
  border-radius: 8px;
  background: ${theme.goldSoft};
  border-left: 4px solid ${theme.goldDark};
  font-size: 0.9rem;
  line-height: 1.5;
  font-weight: 600;
  color: ${theme.ink};
`;

const ConfirmOk = styled(WaSend)`
  background: ${theme.goldDark};
  &:hover {
    background: #74592f;
  }
`;

// Inline group for a meta value plus its chips (voucher QR/WA, email edit + OTA warning).
const ChipRow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  flex-wrap: wrap;
`;

const EditChip = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.22rem;
  border: 1.5px solid ${theme.border};
  border-radius: 7px;
  background: #ffffff;
  color: ${theme.ink700};
  cursor: pointer;
  line-height: 1;
  &:hover {
    background: ${theme.goldSoft};
    border-color: ${theme.goldDark};
    color: ${theme.goldDark};
  }
`;

const OtaWarn = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.12rem 0.45rem;
  border-radius: 6px;
  background: #fef3c7;
  color: #92400e;
  font-size: 0.72rem;
  font-weight: 800;
  cursor: help;
`;

// Reusable "needs attention" glow — pulsing amber. Applied to a value the FO must notice (e.g. an
// OTA-relay email that a paid Xendit refund code will never reach). Pair with <AttnMark> + a Tip.
const attnGlow = keyframes`
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(217, 119, 6, 0);
  }
  50% {
    box-shadow: 0 0 8px 1px rgba(217, 119, 6, 0.6);
  }
`;

const AttnValue = styled.span`
  padding: 0.05rem 0.3rem;
  border-radius: 6px;
  background: ${(props) => (props.$attn ? "#fffbeb" : "transparent")};
  animation: ${(props) => (props.$attn ? attnGlow : "none")} 1.6s ease-in-out
    infinite;
`;

const AttnMark = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  padding: 0.12rem 0.45rem;
  border-radius: 6px;
  background: #fde68a;
  color: #92400e;
  font-size: 0.72rem;
  font-weight: 800;
  cursor: help;
`;

const BookingDrawer = (props) => {
  const { t, lang } = useT();
  const lineId = props.lineId;
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [otaOpen, setOtaOpen] = useState(null);
  // Write-action state: one busy key at a time (double-submit guard), an inline result message, and
  // a dirty flag so closing after any mutation refreshes the page props once.
  const [busyKey, setBusyKey] = useState(null);
  const [actionMsg, setActionMsg] = useState(null);
  const [includeDeposit, setIncludeDeposit] = useState(true);
  const [voucherCode, setVoucherCode] = useState("");
  // Applied booking voucher preview: { code, discount, grand_total, grand_total_excluding_deposit }.
  const [appliedVoucher, setAppliedVoucher] = useState(null);
  const [voucherError, setVoucherError] = useState("");
  const [voucherChecking, setVoucherChecking] = useState(false);
  // Default "include deposit" ON only when the guest checks in today — advance bookings usually pay the
  // deposit later. Applied once on first load (guard ref), then the FO's toggle is respected.
  const depositDefaultRef = useRef(false);
  const [payMethod, setPayMethod] = useState("cash");
  const [depositMethod, setDepositMethod] = useState("cash");
  // Add-ons default to INCLUDED; the set holds the ids the FO has explicitly opted OUT of the next
  // payment. addOnMethod is the cash method for a standalone add-on payment; bedQtyByLine is the
  // per-line "add extra bed" quantity selector.
  const [excludedAddOns, setExcludedAddOns] = useState(() => new Set());
  const [addOnMethod, setAddOnMethod] = useState("cash");
  const [bedQtyByLine, setBedQtyByLine] = useState({});
  const [guideOpen, setGuideOpen] = useState(false);
  // Mirror guideOpen into a ref so the document-level keydown (empty-deps closure below) can read
  // the live value: Escape closes the guide first, and only then the drawer.
  const guideOpenRef = useRef(false);
  const didMutateRef = useRef(false);

  const toggleGuide = (open) => {
    guideOpenRef.current = open;
    setGuideOpen(open);
  };

  const handleClose = () => {
    if (didMutateRef.current) {
      router.reload({ preserveScroll: true });
    }
    props.onClose();
  };

  const act = async (key, path, body = {}, confirmText = null) => {
    if (busyKey) {
      return;
    }
    // Route the confirm through the in-app dialog (not window.confirm) for a consistent look.
    if (confirmText) {
      askConfirm({
        title: t("confirm_generic_title"),
        body: confirmText,
        confirmLabel: t("confirm_ok"),
        onConfirm: () => act(key, path, body),
      });
      return;
    }
    setBusyKey(key);
    setActionMsg(null);
    try {
      const res = await apiFetch(
        `/frontdesk_v2/bookings/${data.booking.id}/${path}`,
        {
          method: "POST",
          body: JSON.stringify({ ...body, line_id: lineId }),
        },
      );
      if (res?.data?.booking) {
        setData(res.data);
        didMutateRef.current = true;
        setActionMsg({ ok: true, text: res.message });
        // Some actions (send voucher) return a wa.me deep link for the FO to send from their WhatsApp.
        if (res.data.wa_url) {
          window.open(res.data.wa_url, "_blank", "noopener,noreferrer");
        }
      } else {
        setActionMsg({ ok: false, text: res?.message || t("error_body") });
      }
    } catch {
      setActionMsg({ ok: false, text: t("error_body") });
    }
    setBusyKey(null);
  };

  // Kill-switch: when OFF the drawer stays read-only (buttons hidden; endpoints refuse server-side).
  const canAct = Boolean(data?.toggles?.drawer_actions_enabled);

  // The drawer is rendered with key={lineId}, so a different line remounts it fresh (skeleton state).
  // reloadTick re-runs the fetch (retry button now; action refetches in the next increment).
  const [reloadTick, setReloadTick] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const refetch = useCallback(() => setReloadTick((tick) => tick + 1), []);
  // Manual refresh (header button): pull fresh booking data — e.g. after the guest says they paid and
  // the Xendit webhook has already updated the status server-side. Shows a spinner until it returns.
  const refresh = useCallback(() => {
    setRefreshing(true);
    refetch();
  }, [refetch]);

  // Enlarged voucher QR (photograph → scan at SelfQ) + send-voucher over WhatsApp (also arms the
  // 10-minute OTP-skip window so the guest walks straight through HGS check-in).
  const [qrOpen, setQrOpen] = useState(false);
  const [waOpen, setWaOpen] = useState(false);
  const [waCountry, setWaCountry] = useState("62");
  const [waManualCode, setWaManualCode] = useState("");
  const [waLocal, setWaLocal] = useState("");
  const [waError, setWaError] = useState("");

  // In-app confirmation dialog: { title, body, note, confirmLabel, onConfirm } or null.
  const [confirmState, setConfirmState] = useState(null);
  const askConfirm = (dialog) => setConfirmState(dialog);
  const runConfirm = () => {
    const dialog = confirmState;
    setConfirmState(null);
    dialog?.onConfirm?.();
  };

  // The RD- code + one-tap claim link always go to the guest's OWN registered contact (FO never sees
  // the code); language follows the FO's current UI language for the message template.
  const sendRefund = (channel) =>
    askConfirm({
      title: `${t("confirm_send_refund_title")} (${channel === "email" ? "Email" : "WhatsApp"})`,
      body: t("confirm_send_refund_body"),
      note: t("confirm_send_refund_note"),
      confirmLabel: t("confirm_send_refund_ok"),
      onConfirm: () =>
        act("send_refund_token", "send_refund_token", {
          channel,
          language: lang,
        }),
    });

  const regenerateRefund = () =>
    askConfirm({
      title: t("confirm_regen_refund_title"),
      body: t("confirm_regen_refund_body"),
      note: t("confirm_regen_refund_note"),
      confirmLabel: t("confirm_regen_refund_ok"),
      onConfirm: () => act("regenerate_refund", "regenerate_refund", {}),
    });

  const generateRefund = () =>
    askConfirm({
      title: t("confirm_generate_refund_title"),
      body: t("confirm_generate_refund_body"),
      note: t("confirm_generate_refund_note"),
      confirmLabel: t("confirm_generate_refund_ok"),
      onConfirm: () => act("generate_refund", "generate_refund", {}),
    });

  // Change / Swap room for a still-reserved line (before check-in). Reuses the /admin flow via
  // RoomMoveModal; on success the modal returns the refreshed booking payload for the drawer.
  const [roomMove, setRoomMove] = useState(null); // { mode: "change"|"swap", line } | null

  const applyRoomMove = (refreshed, message) => {
    setData(refreshed);
    didMutateRef.current = true;
    setActionMsg({ ok: true, text: message });
  };

  // Inline guest-email edit (e.g. replace an OTA-relay address the guest never reads).
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailValue, setEmailValue] = useState("");
  const [emailError, setEmailError] = useState("");

  const openEmailModal = () => {
    setEmailValue(data?.guest?.email || "");
    setEmailError("");
    setEmailOpen(true);
  };

  const submitEmail = () => {
    const email = emailValue.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return setEmailError(t("email_invalid"));
    }
    setEmailError("");
    setEmailOpen(false);
    return act("update_email", "update_email", { email });
  };

  // Inline guest phone / WhatsApp edit (e.g. correct a wrong number so the check-in / refund link lands).
  const [phoneOpen, setPhoneOpen] = useState(false);
  const [phoneValue, setPhoneValue] = useState("");
  const [phoneError, setPhoneError] = useState("");

  const openPhoneModal = () => {
    setPhoneValue(data?.guest?.phone || "");
    setPhoneError("");
    setPhoneOpen(true);
  };

  const submitPhone = () => {
    const phone = phoneValue.replace(/\D/g, "");
    if (phone.length < 8) {
      return setPhoneError(t("phone_invalid"));
    }
    setPhoneError("");
    setPhoneOpen(false);
    return act("update_phone", "update_phone", { phone });
  };

  const openWaModal = () => {
    const detected = detectWaCountry(data?.guest?.phone || "");
    const known = WA_COUNTRIES.some(
      (country) => country.code === detected.code,
    );
    setWaCountry(known ? detected.code : "other");
    setWaManualCode(known ? "" : detected.code);
    setWaLocal(detected.local);
    setWaError("");
    setWaOpen(true);
  };

  const submitWaVoucher = () => {
    const code = (waCountry === "other" ? waManualCode : waCountry).replace(
      /\D/g,
      "",
    );
    const local = waLocal.replace(/\D/g, "").replace(/^0+/, "");
    if (!code) return setWaError(t("wa_err_code"));
    if (!local) return setWaError(t("wa_err_number"));
    setWaError("");
    setWaOpen(false);
    return act("send_wa_voucher", "send_whatsapp_voucher", {
      phone: code + local,
    });
  };

  useEffect(() => {
    let active = true;
    apiFetch(`/frontdesk_v2/lines/${lineId}/booking`)
      .then((res) => {
        if (!active) {
          return;
        }
        if (res?.data?.booking) {
          setData(res.data);
          setError(false);
          if (!depositDefaultRef.current) {
            depositDefaultRef.current = true;
            const today = wibTodayIso();
            const checksInToday = (res.data.lines || []).some(
              (line) =>
                line.check_in_date === today && line.status !== "canceled",
            );
            setIncludeDeposit(checksInToday);
          }
        } else {
          setError(true);
        }
      })
      .catch(() => {
        if (active) {
          setError(true);
        }
      })
      .finally(() => {
        if (active) {
          setRefreshing(false);
        }
      });
    return () => {
      active = false;
    };
  }, [lineId, reloadTick]);

  // The page's 30s auto-refresh (usePoll -> router.reload) fires an Inertia "finish"; refetch the open
  // drawer on it too, so the drawer's data stays as fresh as the lists behind it.
  useEffect(() => {
    const off = router.on("finish", () => refetch());
    return off;
  }, [refetch]);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "Escape") {
        if (guideOpenRef.current) {
          toggleGuide(false);
          return;
        }
        handleClose();
      }
    };
    document.addEventListener("keydown", onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, []);

  const currentPath = () => window.location.pathname;

  // Check-in/out normally happen at the SelfQ machine; these are the manual fallback, so each asks
  // for confirmation (in-app dialog) before firing to avoid a mistaken click moving the guest's status.
  const doCheckIn = (targetLineId) => {
    router.post(
      `/frontdesk_v2/lines/${targetLineId}/check_in`,
      { date: props.date, return_to: currentPath() },
      { preserveScroll: true },
    );
  };

  const doCheckOut = (targetLineId) => {
    router.post(
      `/frontdesk_v2/lines/${targetLineId}/check_out`,
      { date: props.date, return_to: currentPath() },
      { preserveScroll: true },
    );
  };

  const checkIn = (targetLineId) =>
    askConfirm({
      title: t("confirm_checkin_title"),
      body: t("confirm_checkin_body"),
      confirmLabel: t("confirm_checkin_ok"),
      onConfirm: () => doCheckIn(targetLineId),
    });

  const checkOut = (targetLineId) =>
    askConfirm({
      title: t("confirm_checkout_title"),
      body: t("confirm_checkout_body"),
      confirmLabel: t("confirm_checkout_ok"),
      onConfirm: () => doCheckOut(targetLineId),
    });

  // A booking voucher that fully covers the room charge (deposit aside): paying "without deposit"
  // settles the booking for free (no Xendit link) instead of failing.
  const freeVoucherCheckout =
    Boolean(appliedVoucher?.covers_full) && !includeDeposit;

  // Generate the online (Xendit) rate payment link. The confirm spells out whether the deposit is
  // bundled (online deposit ⇒ online refund) so the FO is aware before the link is sent to the guest.
  // When a voucher fully covers the charge the same action settles the booking for free (no link).
  const createPaymentLink = () =>
    askConfirm({
      title: freeVoucherCheckout
        ? t("confirm_free_title")
        : t("confirm_pl_title"),
      body: freeVoucherCheckout
        ? t("confirm_free_body")
        : includeDeposit
          ? t("confirm_pl_with_deposit")
          : t("confirm_pl_without_deposit"),
      note: freeVoucherCheckout ? undefined : t("confirm_pl_note"),
      confirmLabel: freeVoucherCheckout
        ? t("confirm_free_ok")
        : t("confirm_pl_ok"),
      onConfirm: () =>
        act("payment_link", "payment_link", {
          include_deposit: includeDeposit ? "1" : "0",
          voucher_code: appliedVoucher?.code || undefined,
          ...addOnIdsForTarget("room_rate"),
        }),
    });

  // Mark the rate paid at the desk (cash/EDC). Deposit here is a CASH deposit ⇒ refund is cash.
  const markPaidCash = () =>
    askConfirm({
      title: t("confirm_mp_title"),
      body: includeDeposit
        ? t("confirm_mp_with_deposit")
        : t("confirm_mp_without_deposit"),
      note: t("confirm_mp_note"),
      confirmLabel: t("confirm_mp_ok"),
      onConfirm: () =>
        act("mark_paid", "mark_paid", {
          payment_method: payMethod,
          include_deposit: includeDeposit ? "1" : "0",
          voucher_code: appliedVoucher?.code || undefined,
          ...addOnIdsForTarget("room_rate"),
        }),
    });

  // Validate + preview a booking voucher against the current charge. Only an applied voucher is sent
  // when paying, so the FO sees up front whether the code is valid and how much it takes off.
  const applyVoucher = async () => {
    const code = voucherCode.trim();
    if (!code) return;
    setVoucherChecking(true);
    setVoucherError("");
    try {
      const res = await apiFetch(
        `/frontdesk_v2/bookings/${data.booking.id}/preview_voucher`,
        {
          method: "POST",
          body: JSON.stringify({
            voucher_code: code,
            include_deposit: includeDeposit ? "1" : "0",
            ...addOnIdsForTarget("room_rate"),
          }),
        },
      );
      if (res?.data?.code) {
        setAppliedVoucher(res.data);
      } else {
        setAppliedVoucher(null);
        setVoucherError(res?.message || t("voucher_invalid"));
      }
    } catch {
      setAppliedVoucher(null);
      setVoucherError(t("error_body"));
    } finally {
      setVoucherChecking(false);
    }
  };

  const clearVoucher = () => {
    setAppliedVoucher(null);
    setVoucherCode("");
    setVoucherError("");
  };

  // Editing the code invalidates a previously-applied voucher (must re-apply to re-validate).
  const onVoucherInput = (raw) => {
    setVoucherCode(raw.toUpperCase());
    if (appliedVoucher) setAppliedVoucher(null);
    if (voucherError) setVoucherError("");
  };

  // --- Per-line add-ons (extra bed + early check-in) ------------------------------------------------

  // Every pending, still-unattached add-on the FO can fold into the next payment, and which are
  // currently INCLUDED (all of them minus the ids opted out in `excludedAddOns`).
  const addOnPending = () => {
    const rentalIds = [];
    const earlyIds = [];
    (data?.lines || []).forEach((line) => {
      (line.extra_beds || []).forEach((bed) => rentalIds.push(bed.id));
      if (line.early_checkin?.selectable) {
        earlyIds.push(line.early_checkin.id);
      }
    });
    return { rentalIds, earlyIds };
  };

  const hasPendingAddOns = () => {
    const { rentalIds, earlyIds } = addOnPending();
    return rentalIds.length > 0 || earlyIds.length > 0;
  };

  const isAddOnIncluded = (id) => !excludedAddOns.has(id);

  const toggleAddOn = (id) =>
    setExcludedAddOns((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

  // The explicit selection posted to the server. Empty arrays = opt out of every add-on.
  const includedAddOnIds = () => {
    const { rentalIds, earlyIds } = addOnPending();
    return {
      bundled_rental_ids: rentalIds.filter(isAddOnIncluded),
      bundled_early_checkin_ids: earlyIds.filter(isAddOnIncluded),
    };
  };

  // Add-on ids are only sent to the payment flow that currently owns them (add_on_target); on any
  // other flow the key is omitted so the server keeps its default behavior.
  const addOnIdsForTarget = (target) =>
    data.payment?.add_on_target === target ? includedAddOnIds() : {};

  const fmtIdr = (value) => `Rp${Number(value || 0).toLocaleString("en-US")}`;

  // The included add-ons as billable line items (label + amount), for the deposit charge summary.
  const selectedAddOnItems = () => {
    const items = [];
    (data?.lines || []).forEach((line) => {
      const room = line.room_code ? ` ${line.room_code}` : "";
      (line.extra_beds || []).forEach((bed) => {
        if (isAddOnIncluded(bed.id)) {
          items.push({
            key: bed.id,
            label: `${t("addon_extra_bed")}${room} ×${bed.quantity}`,
            rupiah: bed.total_rupiah,
            formatted: bed.total_formatted,
          });
        }
      });
      const early = line.early_checkin;
      if (early?.selectable && isAddOnIncluded(early.id)) {
        items.push({
          key: early.id,
          label: `${t("addon_early")}${room}`,
          rupiah: early.fee_rupiah,
          formatted: early.fee_formatted,
        });
      }
    });
    return items;
  };

  const addExtraBed = (lineId) =>
    act("add_extra_bed", "lines/add_extra_bed", {
      target_line_id: lineId,
      quantity: bedQtyByLine[lineId] || 1,
    });

  const addEarlyCheckin = (line) => {
    const run = () =>
      act("add_early_checkin", "lines/add_early_checkin", {
        target_line_id: line.id,
      });
    if (line.early_checkin?.eligible) {
      run();
    } else {
      askConfirm({
        title: t("addon_early"),
        body: t("addon_early_override_warn"),
        confirmLabel: t("confirm_ok"),
        onConfirm: run,
      });
    }
  };

  const removeAddOnRental = (id) =>
    act("remove_add_on", "lines/remove_add_on", { add_on_rental_id: id });

  const removeEarlyCheckin = (lineId) =>
    act("remove_early_checkin", "lines/remove_early_checkin", {
      target_line_id: lineId,
    });

  // Flow 3: a standalone add-on payment on a fully-settled booking (rate + deposit done).
  const createAddOnLink = () => {
    const ids = includedAddOnIds();
    if (
      !ids.bundled_rental_ids.length &&
      !ids.bundled_early_checkin_ids.length
    ) {
      setActionMsg({ ok: false, text: t("addon_none_selected") });
      return;
    }
    askConfirm({
      title: t("addon_pay_title"),
      body: t("addon_pay_link_body"),
      confirmLabel: t("confirm_ok"),
      onConfirm: () => act("add_on_payment_link", "add_on_payment_link", ids),
    });
  };

  const markAddOnPaidCash = () => {
    const ids = includedAddOnIds();
    if (
      !ids.bundled_rental_ids.length &&
      !ids.bundled_early_checkin_ids.length
    ) {
      setActionMsg({ ok: false, text: t("addon_none_selected") });
      return;
    }
    askConfirm({
      title: t("addon_pay_title"),
      body: t("addon_pay_cash_body"),
      confirmLabel: t("confirm_ok"),
      onConfirm: () =>
        act("mark_add_on_paid", "mark_add_on_paid", {
          ...ids,
          payment_method: addOnMethod,
        }),
    });
  };

  // Send the created online payment link to the guest's WhatsApp (opens wa.me; FO sends from their WA).
  const sendPaymentWa = (url) => {
    const number = waNumber(data.guest?.phone);
    const message = t("wa_payment_msg")
      .replace("%{guest}", data.guest?.full_name || "")
      .replace("%{url}", url);
    window.open(
      `https://wa.me/${number}?text=${encodeURIComponent(message)}`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  const renderPrimary = () => {
    const action = data.primary_action || {};
    switch (action.key) {
      case "collect_payment":
        return (
          <PrimaryCard $bg="#fdf0e1" $border="#f3d3a3">
            <div>
              <PrimaryText>{t("pa_collect_payment")}</PrimaryText>
              <PrimaryHint>{t("pa_collect_payment_hint")}</PrimaryHint>
            </div>
            <PrimaryButton
              type="button"
              onClick={() =>
                document
                  .getElementById("drawer-payment")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
            >
              <Wallet size={15} />
              {t("sec_payment")}
            </PrimaryButton>
          </PrimaryCard>
        );
      case "open_admin_payment":
        return (
          <PrimaryCard $bg="#f3f7fc" $border="#c9dcf2">
            <PrimaryText>{t("pa_open_admin_payment")}</PrimaryText>
            <PrimaryButton
              as="a"
              href={data.booking.admin_url}
              target="_blank"
              rel="noopener"
            >
              <ExternalLink size={15} />
              {t("open_in_admin")}
            </PrimaryButton>
          </PrimaryCard>
        );
      case "check_in":
      case "check_out":
        // Check-in / check-out are done per room in the Kamar section below (no booking-level card).
        return null;
      case "refund_status":
        return (
          <PrimaryCard>
            <PrimaryText>{t("pa_refund_status")}</PrimaryText>
          </PrimaryCard>
        );
      default:
        return (
          <PrimaryCard $bg="#fdecec" $border="#f2c4c0">
            <PrimaryText>{t("pa_canceled")}</PrimaryText>
          </PrimaryCard>
        );
    }
  };

  // Deposit is settled but the guest has not checked in yet: nudge the FO through the QRH next steps
  // (send them to the selfq machine). Cash deposits also get the "hand over the receipt" reminder.
  const renderNextStep = () => {
    const line = data.lines.find(
      (row) => row.status === "reserved" && row.deposit?.status === "paid",
    );
    if (!line) {
      return null;
    }
    const isCash = line.deposit?.method !== "xendit";
    return (
      <NextStepCard>
        <NextStepTitle>
          <CheckCircle2 size={16} />
          {t("ns_title")}
        </NextStepTitle>
        <NextStepIntro>
          {isCash ? t("ns_intro_cash") : t("ns_intro_online")}
        </NextStepIntro>
        <NextStepList>
          <NextStepItem>{t("ns_step_1")}</NextStepItem>
          <NextStepItem>{t("ns_step_2")}</NextStepItem>
          <NextStepItem>{t("ns_step_3")}</NextStepItem>
        </NextStepList>
        {isCash ? <NextStepNote>{t("ns_cash_note")}</NextStepNote> : null}
      </NextStepCard>
    );
  };

  const renderLines = () => (
    <Section>
      <SectionTitle>{t("sec_rooms")}</SectionTitle>
      {data.lines.map((line) => {
        const statusMeta =
          LINE_STATUS_META[line.status] || LINE_STATUS_META.reserved;
        const comment = cleanComment(line.comment);
        return (
          <LineCard
            key={line.id}
            $anchor={line.is_anchor}
            $accent={statusMeta.fg}
          >
            <LineHead>
              <LineRoom>{line.room_code || "—"}</LineRoom>
              {line.room_status ? (
                <StatusBadge
                  meta={physicalStatusMeta(line.room_status)}
                  variant="soft"
                  size={13}
                />
              ) : null}
              <LineType>{line.room_type_name}</LineType>
              <StatusChip $bg={statusMeta.bg} $fg={statusMeta.fg}>
                {t(statusMeta.key)}
              </StatusChip>
              {line.status === "reserved" ? (
                <SmallButton
                  type="button"
                  disabled={!line.can_check_in}
                  title={
                    line.can_check_in
                      ? ""
                      : (line.check_in_blockers || [])
                          .map((blocker) => t(`blk_${blocker}`))
                          .join(" · ")
                  }
                  onClick={() => checkIn(line.id)}
                >
                  <LogIn size={13} />
                  {t("manual_checkin_action")}
                </SmallButton>
              ) : null}
              {line.status === "checked_in" ? (
                <SmallButton
                  type="button"
                  disabled={!line.can_check_out}
                  onClick={() => checkOut(line.id)}
                >
                  <LogOut size={13} />
                  {t("manual_checkout_action")}
                </SmallButton>
              ) : null}
            </LineHead>
            <LineDates>
              {formatDate(line.check_in_date, lang, false)} →{" "}
              {formatDate(line.check_out_date, lang, false)}
              {line.nights ? ` · ${line.nights} ${t("nights")}` : ""}
              {line.wifi_usernames?.length
                ? ` · ${t("wifi_label")}: ${line.wifi_usernames.join(", ")}`
                : ""}
            </LineDates>
            {line.deposit ? (
              <LineDeposit>
                <span>
                  {t("bd_deposit")}: {line.deposit.amount_formatted}
                </span>
                <PayBadge status={line.deposit.status} />
              </LineDeposit>
            ) : null}
            {line.status === "reserved" &&
            !line.can_check_in &&
            line.check_in_blockers?.length ? (
              <Blockers>
                {t("checkin_blocked_title")}{" "}
                {line.check_in_blockers
                  .map((blocker) => t(`blk_${blocker}`))
                  .join(" · ")}
              </Blockers>
            ) : null}
            <NotesRow>
              <FoNotesButton
                counts={data.notes_counts?.[line.id]}
                onClick={() =>
                  props.onOpenComments({
                    id: line.id,
                    room_code: line.room_code,
                    guest_name: data.guest.full_name,
                  })
                }
              />
              {comment ? (
                <GuestNotesButton
                  hasNote
                  active={otaOpen === line.id}
                  onClick={() =>
                    setOtaOpen(otaOpen === line.id ? null : line.id)
                  }
                />
              ) : null}
              {canAct && line.status === "reserved" ? (
                <RoomMoveButtons>
                  <ChangeRoomButton
                    type="button"
                    disabled={busyKey !== null}
                    onClick={() => setRoomMove({ mode: "change", line })}
                  >
                    <RotateCw size={13} />
                    {t("act_change_room")}
                  </ChangeRoomButton>
                  <ChangeRoomButton
                    type="button"
                    disabled={busyKey !== null}
                    onClick={() => setRoomMove({ mode: "swap", line })}
                  >
                    <ArrowLeftRight size={13} />
                    {t("act_swap_room")}
                  </ChangeRoomButton>
                </RoomMoveButtons>
              ) : null}
            </NotesRow>
            {otaOpen === line.id ? <OtaBox>{comment}</OtaBox> : null}
            {renderLineAddOns(line)}
          </LineCard>
        );
      })}
    </Section>
  );

  // Per-line add-ons: list pending extra beds + early check-in with an include toggle + remove, plus
  // the add controls. Only when the drawer can act, the line isn't canceled, and some payment flow can
  // actually carry the charge (add_on_target !== "none").
  const renderLineAddOns = (line) => {
    if (
      !canAct ||
      line.status === "canceled" ||
      data.payment?.add_on_target === "none"
    ) {
      return null;
    }
    const early = line.early_checkin || {};
    const canAddEarly = !early.exists && (early.eligible || early.can_override);
    return (
      <AddOnBlock>
        <AddOnLine>
          <AddOnLabel>
            <BedDouble size={13} />
            {t("addon_title")}
          </AddOnLabel>
        </AddOnLine>
        {(line.extra_beds || []).map((bed) => (
          <AddOnLine key={bed.id}>
            <DepositCheck
              type="button"
              $on={isAddOnIncluded(bed.id)}
              aria-pressed={isAddOnIncluded(bed.id)}
              onClick={() => toggleAddOn(bed.id)}
            >
              <DepositBox $on={isAddOnIncluded(bed.id)}>
                {isAddOnIncluded(bed.id) ? <Check size={13} /> : null}
              </DepositBox>
              {t("addon_extra_bed")} ×{bed.quantity}
            </DepositCheck>
            <AddOnPrice>{bed.total_formatted}</AddOnPrice>
            <TinyButton
              type="button"
              $danger
              disabled={busyKey !== null}
              onClick={() => removeAddOnRental(bed.id)}
            >
              <Trash2 size={12} />
              {t("addon_remove")}
            </TinyButton>
          </AddOnLine>
        ))}
        {early.selectable ? (
          <AddOnLine>
            <DepositCheck
              type="button"
              $on={isAddOnIncluded(early.id)}
              aria-pressed={isAddOnIncluded(early.id)}
              onClick={() => toggleAddOn(early.id)}
            >
              <DepositBox $on={isAddOnIncluded(early.id)}>
                {isAddOnIncluded(early.id) ? <Check size={13} /> : null}
              </DepositBox>
              {t("addon_early")}
            </DepositCheck>
            <AddOnPrice>{early.fee_formatted}</AddOnPrice>
            <TinyButton
              type="button"
              $danger
              disabled={busyKey !== null}
              onClick={() => removeEarlyCheckin(line.id)}
            >
              <Trash2 size={12} />
              {t("addon_remove")}
            </TinyButton>
          </AddOnLine>
        ) : early.exists && early.paid ? (
          <AddOnLine>
            <AddOnLabel>
              <Clock size={13} />
              {t("addon_early")}
            </AddOnLabel>
            <PayBadge status="paid" />
          </AddOnLine>
        ) : null}
        <AddOnLine>
          {line.can_add_extra_bed ? (
            <>
              <MethodSelect
                value={bedQtyByLine[line.id] || 1}
                onChange={(event) =>
                  setBedQtyByLine({
                    ...bedQtyByLine,
                    [line.id]: Number(event.target.value),
                  })
                }
              >
                {[1, 2, 3, 4, 5].map((qty) => (
                  <option key={qty} value={qty}>
                    {qty}
                  </option>
                ))}
              </MethodSelect>
              <TinyButton
                type="button"
                disabled={busyKey !== null}
                onClick={() => addExtraBed(line.id)}
              >
                <Plus size={12} />
                {t("addon_add_bed")}
              </TinyButton>
            </>
          ) : null}
          {canAddEarly ? (
            <TinyButton
              type="button"
              disabled={busyKey !== null}
              onClick={() => addEarlyCheckin(line)}
            >
              <Plus size={12} />
              {t("addon_add_early")}
              {!early.eligible ? <AlertTriangle size={12} /> : null}
            </TinyButton>
          ) : null}
        </AddOnLine>
      </AddOnBlock>
    );
  };

  // One payment row (room-rate, deposit-collection, or add-on-only), shared by the payment + add-on
  // sections. The header label is chosen from the row's kind.
  const payRow = (row) => {
    const label = row.add_on_only
      ? t("sec_add_on")
      : row.deposit_collection
        ? t("sec_deposit")
        : t("sec_payment");
    return (
      <PayItem key={row.id}>
        <PayItemTop>
          <span>{label}</span>
          <PayMeta>
            <ProviderChip $xendit={row.provider === "xendit"}>
              {row.provider === "xendit" ? (
                <Zap size={11} />
              ) : (
                <Banknote size={11} />
              )}
              {row.provider === "xendit"
                ? "Xendit"
                : row.payment_method || t("provider_manual")}
            </ProviderChip>
            <PayBadge status={row.status} />
          </PayMeta>
        </PayItemTop>
        <PayItemBottom>
          <strong>{row.amount_formatted}</strong>
          {row.payment_link_url && row.status === "pending" ? (
            <>
              <PdfLink
                href={row.payment_link_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLink size={12} /> {t("open_link")}
              </PdfLink>
              <WaSendButton
                type="button"
                onClick={() => sendPaymentWa(row.payment_link_url)}
              >
                <MessageCircle size={12} /> {t("send_payment_wa")}
              </WaSendButton>
            </>
          ) : null}
          {row.invoice_no ? (
            <PayInvoice>
              {t("invoice_no")}: {row.invoice_no}
            </PayInvoice>
          ) : null}
          {row.invoice_pdf ? (
            <>
              <PdfLink
                href={`/admin/online_payments/${row.id}/invoice_pdf?type=RoomBookingPayment`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <FileText size={12} /> {t("view_pdf")}
              </PdfLink>
              <PdfLink
                href={`/admin/online_payments/${row.id}/invoice_pdf?type=RoomBookingPayment&download=1`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Download size={12} /> {t("download_pdf")}
              </PdfLink>
            </>
          ) : null}
        </PayItemBottom>
      </PayItem>
    );
  };

  const renderPayment = () => {
    const payment = data.payment;
    return (
      <Section id="drawer-payment">
        <SectionTitle>{t("sec_payment")}</SectionTitle>
        <KV>
          <span>Status</span>
          <PayBadge
            status={data.booking.booking_rate_paid ? "paid" : "unpaid"}
          />
        </KV>
        <KV>
          <span>{t("total_paid")}</span>
          <strong>{data.booking.total_paid_formatted}</strong>
        </KV>
        {payment.breakdown ? (
          <>
            <KV>
              <span>{t("bd_rooms")}</span>
              <strong>{payment.breakdown.rooms_total.formatted}</strong>
            </KV>
            <KV>
              <span>{t("bd_addons")}</span>
              <strong>{payment.breakdown.add_ons_total.formatted}</strong>
            </KV>
            <KV>
              <span>{t("bd_early")}</span>
              <strong>
                {payment.breakdown.early_checkins_total.formatted}
              </strong>
            </KV>
            <KV>
              <span>{t("bd_deposit")}</span>
              <DepositAmount $off={!includeDeposit}>
                {payment.breakdown.deposit_total.formatted}
              </DepositAmount>
            </KV>
            {appliedVoucher ? (
              <KV>
                <span>
                  {t("bd_voucher_discount")} ({appliedVoucher.code})
                </span>
                <VoucherOk>−{appliedVoucher.discount}</VoucherOk>
              </KV>
            ) : null}
            <KV>
              <span>{t("bd_total")}</span>
              <strong>
                {appliedVoucher
                  ? includeDeposit
                    ? appliedVoucher.grand_total
                    : appliedVoucher.grand_total_excluding_deposit
                  : includeDeposit
                    ? payment.breakdown.grand_total.formatted
                    : payment.breakdown.grand_total_excluding_deposit.formatted}
              </strong>
            </KV>
            {hasPendingAddOns() ? (
              <AddOnNote>{t("addon_include_note")}</AddOnNote>
            ) : null}
          </>
        ) : null}
        {payment.payments.map((row) => payRow(row))}
        {canAct &&
        data.booking.manual_direct_booking &&
        !data.booking.booking_rate_paid &&
        !payment.has_active_link ? (
          <VoucherRow>
            <VoucherLabel>{t("voucher_placeholder")}</VoucherLabel>
            <VoucherInput
              type="text"
              value={voucherCode}
              onChange={(event) => onVoucherInput(event.target.value)}
              placeholder={t("voucher_placeholder")}
              aria-label={t("voucher_placeholder")}
              disabled={Boolean(appliedVoucher)}
            />
            {appliedVoucher ? (
              <SmallButton type="button" onClick={clearVoucher}>
                {t("voucher_remove")}
              </SmallButton>
            ) : (
              <SmallButton
                type="button"
                disabled={voucherChecking || !voucherCode.trim()}
                onClick={applyVoucher}
              >
                {voucherChecking ? t("voucher_checking") : t("voucher_apply")}
              </SmallButton>
            )}
            {appliedVoucher ? (
              <VoucherOk>
                ✓ {t("voucher_applied")}: −{appliedVoucher.discount}
              </VoucherOk>
            ) : null}
            {voucherError ? <VoucherErr>{voucherError}</VoucherErr> : null}
          </VoucherRow>
        ) : null}
        {canAct &&
        data.booking.manual_direct_booking &&
        !data.booking.booking_rate_paid ? (
          <ActionBar>
            {!payment.has_active_link ? (
              <DepositCheck
                type="button"
                $on={includeDeposit}
                aria-pressed={includeDeposit}
                onClick={() => setIncludeDeposit(!includeDeposit)}
              >
                <DepositBox $on={includeDeposit}>
                  {includeDeposit ? <Check size={13} /> : null}
                </DepositBox>
                {t("include_deposit_label")}
              </DepositCheck>
            ) : null}
            {payment.has_active_link ? (
              <SmallButton
                type="button"
                disabled={busyKey !== null}
                onClick={() => act("sync_payment", "sync_payment")}
              >
                {t("act_sync")}
              </SmallButton>
            ) : (
              <SmallButton
                type="button"
                disabled={busyKey !== null}
                onClick={createPaymentLink}
              >
                {freeVoucherCheckout
                  ? t("act_finish_free")
                  : t("act_payment_link")}
              </SmallButton>
            )}
            {data.toggles.can_use_manual_cash ? (
              <>
                <MethodSelect
                  value={payMethod}
                  onChange={(event) => setPayMethod(event.target.value)}
                >
                  <option value="cash">Cash</option>
                  <option value="edc">EDC</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="qris">QRIS</option>
                </MethodSelect>
                <SmallButton
                  type="button"
                  disabled={busyKey !== null}
                  onClick={markPaidCash}
                >
                  {t("act_mark_paid")}
                </SmallButton>
              </>
            ) : null}
          </ActionBar>
        ) : null}
        {!data.booking.manual_direct_booking &&
        !data.booking.booking_rate_paid ? (
          <InfoNote>
            <Info size={14} />
            <span>
              {t("pa_open_admin_payment")}{" "}
              <a
                href={data.booking.admin_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("open_in_admin")}
              </a>
            </span>
          </InfoNote>
        ) : null}
      </Section>
    );
  };

  // Deposit charge summary: deposit + the ticked add-ons + the total the FO will collect. Shown once
  // an add-on is staged on this booking (flow 2), so the payable amount is unambiguous.
  const renderDepositSummary = () => {
    const deposit = data.deposit;
    if (data.payment?.add_on_target !== "deposit" || !hasPendingAddOns()) {
      return null;
    }
    const items = selectedAddOnItems();
    const addOnsTotal = items.reduce(
      (sum, item) => sum + Number(item.rupiah || 0),
      0,
    );
    const cashTotal = Number(deposit.due_rupiah || 0) + addOnsTotal;
    const onlineTotal = cashTotal + Number(deposit.online_fee_rupiah || 0);
    return (
      <SummaryBox>
        <SummaryHead>
          <FileText size={14} />
          {t("dep_summary_title")}
        </SummaryHead>
        <SummaryRow>
          <span>
            {t("bd_deposit")} ({deposit.unpaid_count})
          </span>
          <span>{deposit.due_formatted}</span>
        </SummaryRow>
        {items.map((item) => (
          <SummaryRow key={item.key}>
            <span>{item.label}</span>
            <span>{item.formatted}</span>
          </SummaryRow>
        ))}
        {items.length === 0 ? (
          <SummaryRow $muted>
            <span>{t("dep_summary_none")}</span>
            <span>—</span>
          </SummaryRow>
        ) : null}
        <SummaryDivider />
        <SummaryRow $total>
          <span>{t("dep_summary_cash_total")}</span>
          <span>{fmtIdr(cashTotal)}</span>
        </SummaryRow>
        <SummaryRow $muted>
          <span>{t("dep_summary_online_total")}</span>
          <span>{fmtIdr(onlineTotal)}</span>
        </SummaryRow>
        <AddOnNote>
          {t("dep_summary_fee_note").replace(
            "%{fee}",
            deposit.online_fee_formatted,
          )}
        </AddOnNote>
      </SummaryBox>
    );
  };

  const renderDeposit = () => {
    const deposit = data.deposit;
    const showManual =
      data.toggles.can_use_manual_cash_deposit && !deposit.locked;
    return (
      <Section>
        <SectionTitle>{t("sec_deposit")}</SectionTitle>
        {deposit.all_settled ? (
          <KV>
            <span>{t("deposit_settled")}</span>
          </KV>
        ) : (
          <>
            <KV>
              <span>
                {t("deposit_due")} ({deposit.unpaid_count})
              </span>
              <strong>{deposit.due_formatted}</strong>
            </KV>
            {renderDepositSummary()}

            <SubSection>
              <SubHead>
                <SubTitle>
                  <Globe size={13} />
                  {t("dep_online_title")}
                </SubTitle>
                <GuideLink type="button" onClick={() => toggleGuide(true)}>
                  <BookOpen size={13} />
                  {t("dep_guide_btn")}
                </GuideLink>
              </SubHead>
              {deposit.locked ? (
                <InfoNote>
                  <Info size={14} />
                  <span>{t("deposit_locked_note")}</span>
                </InfoNote>
              ) : (
                <>
                  <KV>
                    <span>{t("dep_online_fee")}</span>
                    <strong>{deposit.online_fee_formatted}</strong>
                  </KV>
                  {deposit.active_link ? (
                    <>
                      <KV>
                        <span>Link</span>
                        <strong>
                          <a
                            href={deposit.active_link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {t("dep_open_link")}
                          </a>
                        </strong>
                      </KV>
                      {deposit.active_link.expires_at ? (
                        <KV>
                          <span>{t("dep_link_expires")}</span>
                          <strong>
                            {formatDateTime(
                              deposit.active_link.expires_at,
                              lang,
                            )}
                          </strong>
                        </KV>
                      ) : null}
                      {canAct ? (
                        <SubActions>
                          <SmallButton
                            type="button"
                            disabled={busyKey !== null}
                            onClick={() => act("sync_deposit", "sync_deposit")}
                          >
                            {t("act_sync")}
                          </SmallButton>
                        </SubActions>
                      ) : null}
                    </>
                  ) : canAct ? (
                    <SubActions>
                      <SmallButton
                        type="button"
                        $emph
                        disabled={busyKey !== null}
                        onClick={() =>
                          askConfirm({
                            title: t("confirm_deposit_link_title"),
                            body: t("confirm_deposit_link_body"),
                            note: t("confirm_deposit_link_note"),
                            confirmLabel: t("confirm_deposit_link_ok"),
                            onConfirm: () =>
                              act("deposit_link", "deposit_link", {
                                ...addOnIdsForTarget("deposit"),
                              }),
                          })
                        }
                      >
                        {t("act_deposit_link")}
                      </SmallButton>
                    </SubActions>
                  ) : null}
                  <InfoNote>
                    <Info size={14} />
                    <span>{t("dep_online_note")}</span>
                  </InfoNote>
                  {canAct ? null : (
                    <InfoNote>
                      <Info size={14} />
                      <span>{t("dep_actions_disabled_note")}</span>
                    </InfoNote>
                  )}
                </>
              )}
            </SubSection>

            {showManual ? (
              <SubSection>
                <SubHead>
                  <SubTitle>
                    <Banknote size={13} />
                    {t("dep_manual_title")}
                  </SubTitle>
                </SubHead>
                {canAct ? (
                  <SubActions>
                    {deposit.active_link ? (
                      <SmallButton
                        type="button"
                        disabled={busyKey !== null}
                        onClick={() =>
                          act(
                            "cancel_deposit_link",
                            "cancel_deposit_link",
                            {},
                            t("confirm_switch_cash"),
                          )
                        }
                      >
                        {t("act_switch_cash")}
                      </SmallButton>
                    ) : (
                      <>
                        <MethodSelect
                          value={depositMethod}
                          onChange={(event) =>
                            setDepositMethod(event.target.value)
                          }
                        >
                          <option value="cash">Cash</option>
                          <option value="edc">EDC</option>
                          <option value="bank_transfer">Bank Transfer</option>
                          <option value="qris">QRIS</option>
                        </MethodSelect>
                        <SmallButton
                          type="button"
                          $emph
                          disabled={busyKey !== null}
                          onClick={() =>
                            askConfirm({
                              title: `${t("confirm_deposit_cash_title")} (${t(
                                `method_${depositMethod}`,
                              )})`,
                              body: t("confirm_deposit_cash_body"),
                              note: t("confirm_deposit_cash_note"),
                              confirmLabel: t("confirm_deposit_cash_ok"),
                              onConfirm: () =>
                                act("deposit_cash", "deposit_cash", {
                                  payment_method: depositMethod,
                                  ...addOnIdsForTarget("deposit"),
                                }),
                            })
                          }
                        >
                          {t("act_deposit_cash")}
                        </SmallButton>
                      </>
                    )}
                  </SubActions>
                ) : null}
                <InfoNote>
                  <Info size={14} />
                  <span>{t("dep_manual_note")}</span>
                </InfoNote>
              </SubSection>
            ) : null}
          </>
        )}
      </Section>
    );
  };

  // Flow 3: a standalone add-on payment once the booking is fully settled (rate + deposit done).
  // Mirrors the room-payment lifecycle: create link / mark paid cash / sync / regenerate / cancel.
  const renderAddOnPayment = () => {
    const payment = data.payment;
    if (!payment.can_sell_add_ons) {
      return null;
    }
    const rows = payment.add_on_payments || [];
    const latest = rows[0];
    const canRegen =
      latest &&
      ["expired", "failed"].includes(latest.status) &&
      !payment.has_active_add_on_link;
    return (
      <Section>
        <SectionTitle>{t("sec_add_on")}</SectionTitle>
        {rows.length === 0 ? (
          <KV>
            <span>{t("addon_none_yet")}</span>
          </KV>
        ) : (
          rows.map((row) => payRow(row))
        )}
        {canAct ? (
          payment.has_active_add_on_link ? (
            <ActionBar>
              <SmallButton
                type="button"
                disabled={busyKey !== null}
                onClick={() =>
                  act("sync_add_on_payment", "sync_add_on_payment")
                }
              >
                {t("act_sync")}
              </SmallButton>
              <SmallButton
                type="button"
                disabled={busyKey !== null}
                onClick={() =>
                  act(
                    "cancel_add_on_payment",
                    "cancel_add_on_payment",
                    {},
                    t("addon_cancel_confirm"),
                  )
                }
              >
                {t("act_cancel_link")}
              </SmallButton>
            </ActionBar>
          ) : hasPendingAddOns() ? (
            <ActionBar>
              <SmallButton
                type="button"
                disabled={busyKey !== null}
                onClick={createAddOnLink}
              >
                {t("act_add_on_link")}
              </SmallButton>
              {canRegen ? (
                <SmallButton
                  type="button"
                  disabled={busyKey !== null}
                  onClick={() =>
                    act(
                      "regenerate_add_on_payment",
                      "regenerate_add_on_payment",
                    )
                  }
                >
                  {t("act_regenerate")}
                </SmallButton>
              ) : null}
              {data.toggles.can_use_manual_cash ? (
                <>
                  <MethodSelect
                    value={addOnMethod}
                    onChange={(event) => setAddOnMethod(event.target.value)}
                  >
                    <option value="cash">Cash</option>
                    <option value="edc">EDC</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="qris">QRIS</option>
                  </MethodSelect>
                  <SmallButton
                    type="button"
                    disabled={busyKey !== null}
                    onClick={markAddOnPaidCash}
                  >
                    {t("act_mark_paid")}
                  </SmallButton>
                </>
              ) : null}
            </ActionBar>
          ) : canRegen ? (
            <ActionBar>
              <SmallButton
                type="button"
                disabled={busyKey !== null}
                onClick={() =>
                  act("regenerate_add_on_payment", "regenerate_add_on_payment")
                }
              >
                {t("act_regenerate")}
              </SmallButton>
            </ActionBar>
          ) : (
            <InfoNote>
              <Info size={14} />
              <span>{t("addon_add_hint")}</span>
            </InfoNote>
          )
        ) : null}
      </Section>
    );
  };

  const renderRefund = () => {
    const refund = data.refund;
    const hasAny = refund.states.some((state) => state.state !== "none");
    return (
      <Section>
        <SectionTitle>{t("sec_refund")}</SectionTitle>
        {hasAny ? (
          <>
            <KV>
              <span>{t("rf_refundable")}</span>
              <strong>{refund.refundable_now_formatted}</strong>
            </KV>
            <KV>
              <span>{t("rf_review")}</span>
              <strong>{refund.pending_review_formatted}</strong>
            </KV>
            {refund.latest_batch ? (
              <KV>
                <span>{t("rf_batch")}</span>
                <strong>{refund.latest_batch.status}</strong>
              </KV>
            ) : null}
            {refund.states
              .filter((state) => state.state !== "none")
              .map((state) => {
                const meta = REFUND_STATE_META[state.state];
                return (
                  <Fragment key={state.line_id}>
                    <KV>
                      <span>{state.room_code || "—"}</span>
                      {meta ? (
                        <StatusChip $bg={meta.bg} $fg={meta.fg}>
                          {t(meta.key)}
                        </StatusChip>
                      ) : (
                        <strong>
                          {String(state.state).replace(/_/g, " ")}
                        </strong>
                      )}
                    </KV>
                    {canAct && state.state === "refundable_cash" ? (
                      <ActionBar>
                        <SmallButton
                          type="button"
                          disabled={busyKey !== null}
                          onClick={() =>
                            askConfirm({
                              title: t("confirm_record_cash_refund_title"),
                              body: t("confirm_record_cash_refund_body"),
                              note: t("confirm_record_cash_refund_note"),
                              confirmLabel: t("confirm_record_cash_refund_ok"),
                              onConfirm: () =>
                                act(
                                  "record_cash_refund",
                                  "record_cash_refund",
                                  {
                                    refund_line_id: state.line_id,
                                    cash_refund_confirmed: true,
                                  },
                                ),
                            })
                          }
                        >
                          <Banknote size={13} />
                          {t("act_record_cash_refund")}
                          {state.cash_refund_amount
                            ? ` · ${state.cash_refund_amount}`
                            : ""}
                        </SmallButton>
                      </ActionBar>
                    ) : null}
                  </Fragment>
                );
              })}
            {refund.online_refund_outstanding ? (
              <InfoNote>{t("dep_online_note")}</InfoNote>
            ) : null}
            {canAct && !refund.online && refund.can_generate ? (
              <>
                <ActionBar>
                  <SmallButton
                    type="button"
                    disabled={busyKey !== null}
                    onClick={generateRefund}
                  >
                    <RefreshCw size={13} />
                    {t("act_generate_refund")}
                  </SmallButton>
                </ActionBar>
                <InfoNote>{t("rf_generate_hint")}</InfoNote>
              </>
            ) : null}
            {refund.online ? (
              <>
                <KV>
                  <span>{t("rf_online_status")}</span>
                  <StatusChip
                    $bg={
                      ONLINE_REFUND_META[refund.online.status]?.bg || "#eef1f5"
                    }
                    $fg={
                      ONLINE_REFUND_META[refund.online.status]?.fg ||
                      theme.ink700
                    }
                  >
                    {t(`rfo_${refund.online.status}`)}
                  </StatusChip>
                </KV>
                {refund.online.expires_at ? (
                  <InfoNote>
                    {t("rf_expires")}:{" "}
                    {formatDateTime(refund.online.expires_at, lang)}
                  </InfoNote>
                ) : null}
                {canAct ? (
                  <>
                    {refund.online.code_sendable ? (
                      <ActionBar>
                        <SmallButton
                          type="button"
                          disabled={busyKey !== null}
                          onClick={() => sendRefund("whatsapp")}
                        >
                          <MessageCircle size={13} />
                          {t("act_send_wa")}
                        </SmallButton>
                        <SmallButton
                          type="button"
                          disabled={busyKey !== null}
                          onClick={() => sendRefund("email")}
                        >
                          <Mail size={13} />
                          {t("act_send_email")}
                        </SmallButton>
                      </ActionBar>
                    ) : null}
                    {refund.online.can_sync || refund.online.can_regenerate ? (
                      <ActionBar>
                        {refund.online.can_sync ? (
                          <SmallButton
                            type="button"
                            disabled={busyKey !== null}
                            onClick={() =>
                              act("sync_refund", "sync_refund", {})
                            }
                          >
                            <RotateCw size={13} />
                            {t("act_sync_refund")}
                          </SmallButton>
                        ) : null}
                        {refund.online.can_regenerate ? (
                          <SmallButton
                            type="button"
                            disabled={busyKey !== null}
                            onClick={regenerateRefund}
                          >
                            <RefreshCw size={13} />
                            {t("act_regenerate_refund")}
                          </SmallButton>
                        ) : null}
                      </ActionBar>
                    ) : null}
                    <InfoNote>{t("rf_send_hint")}</InfoNote>
                  </>
                ) : null}
              </>
            ) : null}
          </>
        ) : (
          <KV>
            <span>{t("rf_none")}</span>
          </KV>
        )}
      </Section>
    );
  };

  return (
    <>
      <Overlay onClick={handleClose} />
      <Panel role="dialog" aria-modal="true" aria-label={t("drawer_title")}>
        <Head>
          <div>
            <HeadGuest>
              {data ? data.guest.full_name || "—" : t("loading")}
            </HeadGuest>
            {data ? (
              <HeadMeta>
                <MetaLabel>{t("voucher")}</MetaLabel>
                <MetaValue>
                  <ChipRow>
                    <span>{data.booking.voucher_code || "—"}</span>
                    {data.booking.voucher_code && data.booking.voucher_qr ? (
                      <TipWrap>
                        <ChipButton
                          type="button"
                          onClick={() => setQrOpen(true)}
                          aria-label={t("show_qr")}
                        >
                          <QrCode size={14} /> QR
                        </ChipButton>
                        <Tip className="fdtip">{t("qr_tip")}</Tip>
                      </TipWrap>
                    ) : null}
                    {data.booking.voucher_code && canAct ? (
                      <TipWrap>
                        <ChipButton
                          type="button"
                          onClick={openWaModal}
                          disabled={busyKey === "send_wa_voucher"}
                          aria-label={t("wa_voucher")}
                        >
                          <MessageCircle size={14} /> WA
                        </ChipButton>
                        <Tip className="fdtip">{t("wa_tip")}</Tip>
                      </TipWrap>
                    ) : null}
                  </ChipRow>
                </MetaValue>

                <MetaLabel>{t("source")}</MetaLabel>
                <MetaValue>{data.booking.source || "—"}</MetaValue>

                <MetaLabel>{t("phone")}</MetaLabel>
                <MetaValue>
                  <ChipRow>
                    <span>{data.guest.phone || "—"}</span>
                    {canAct ? (
                      <TipWrap>
                        <EditChip
                          type="button"
                          onClick={openPhoneModal}
                          disabled={busyKey === "update_phone"}
                          aria-label={t("phone_edit")}
                        >
                          <Pencil size={13} />
                        </EditChip>
                        <Tip className="fdtip">{t("phone_edit_tip")}</Tip>
                      </TipWrap>
                    ) : null}
                  </ChipRow>
                </MetaValue>

                <MetaLabel>{t("email")}</MetaLabel>
                <MetaValue>
                  <ChipRow>
                    <AttnValue $attn={data.guest.email_attention}>
                      {data.guest.email || "—"}
                    </AttnValue>
                    {canAct ? (
                      <TipWrap>
                        <EditChip
                          type="button"
                          onClick={openEmailModal}
                          disabled={busyKey === "update_email"}
                          aria-label={t("email_edit")}
                        >
                          <Pencil size={13} />
                        </EditChip>
                        <Tip className="fdtip">{t("email_edit_tip")}</Tip>
                      </TipWrap>
                    ) : null}
                    {data.guest.email_attention ? (
                      <TipWrap>
                        <AttnMark>
                          <AlertTriangle size={12} />{" "}
                          {t("email_attention_badge")}
                        </AttnMark>
                        <Tip className="fdtip">{t("email_attention_tip")}</Tip>
                      </TipWrap>
                    ) : data.guest.email_is_ota ? (
                      <TipWrap>
                        <OtaWarn>
                          <AlertTriangle size={12} /> {t("email_ota_badge")}
                        </OtaWarn>
                        <Tip className="fdtip">{t("email_ota_tip")}</Tip>
                      </TipWrap>
                    ) : null}
                  </ChipRow>
                </MetaValue>
              </HeadMeta>
            ) : null}
          </div>
          <HeadActions>
            <RefreshButton
              type="button"
              data-spin={refreshing ? "true" : "false"}
              disabled={refreshing}
              aria-label={t("drawer_refresh")}
              title={t("drawer_refresh")}
              onClick={refresh}
            >
              <RotateCw size={15} />
            </RefreshButton>
            {data ? (
              <AdminLink
                href={data.booking.admin_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLink size={13} />
                {t("open_in_admin")}
              </AdminLink>
            ) : null}
            <CloseButton
              type="button"
              aria-label={t("close")}
              onClick={handleClose}
            >
              <X size={16} />
            </CloseButton>
          </HeadActions>
        </Head>
        <Body>
          {error ? (
            <ErrorBox>
              {t("error_body")}
              <div>
                <RetryButton
                  type="button"
                  onClick={() => {
                    setError(false);
                    refetch();
                  }}
                >
                  {t("retry")}
                </RetryButton>
              </div>
            </ErrorBox>
          ) : data ? (
            <>
              {actionMsg ? (
                <ActionMsg $ok={actionMsg.ok}>{actionMsg.text}</ActionMsg>
              ) : null}
              {renderPrimary()}
              {renderNextStep()}
              {renderLines()}
              {renderPayment()}
              {renderDeposit()}
              {renderAddOnPayment()}
              {renderRefund()}
            </>
          ) : (
            <>
              <SkeletonBlock $h="64px" />
              <SkeletonBlock $h="160px" />
              <SkeletonBlock $h="120px" />
              <SkeletonBlock $h="90px" />
            </>
          )}
        </Body>
      </Panel>
      {guideOpen ? (
        <DepositGuideModal onClose={() => toggleGuide(false)} />
      ) : null}
      {qrOpen && data?.booking?.voucher_qr ? (
        <QrBackdrop onClick={() => setQrOpen(false)}>
          <QrCard onClick={(event) => event.stopPropagation()}>
            <QrLargeImg src={data.booking.voucher_qr} alt={t("voucher")} />
            <QrCodeText>{data.booking.voucher_code}</QrCodeText>
            <QrHint>{t("qr_hint")}</QrHint>
            <QrCloseButton type="button" onClick={() => setQrOpen(false)}>
              {t("close")}
            </QrCloseButton>
          </QrCard>
        </QrBackdrop>
      ) : null}
      {waOpen && data?.booking ? (
        <QrBackdrop onClick={() => setWaOpen(false)}>
          <WaCard onClick={(event) => event.stopPropagation()}>
            <WaTitle>{t("wa_voucher")}</WaTitle>
            <WaDesc>
              {t("wa_modal_desc")} <strong>{data.booking.voucher_code}</strong>
            </WaDesc>
            <WaRow>
              <WaSelect
                value={waCountry}
                onChange={(event) => setWaCountry(event.target.value)}
              >
                {WA_COUNTRIES.map((country) => (
                  <option key={country.code} value={country.code}>
                    {country.flag} +{country.code} {country.name}
                  </option>
                ))}
                <option value="other">🌐 {t("wa_other")}</option>
              </WaSelect>
              <WaInput
                type="text"
                inputMode="numeric"
                autoFocus
                placeholder="81234567890"
                value={waLocal}
                onChange={(event) => setWaLocal(event.target.value)}
              />
            </WaRow>
            {waCountry === "other" ? (
              <WaInput
                style={{ marginTop: "0.5rem", width: "100%" }}
                type="text"
                inputMode="numeric"
                placeholder={t("wa_code_placeholder")}
                value={waManualCode}
                onChange={(event) => setWaManualCode(event.target.value)}
              />
            ) : null}
            <WaHint>{t("wa_number_hint")}</WaHint>
            {waError ? <WaErr>{waError}</WaErr> : null}
            <WaActions>
              <WaCancel type="button" onClick={() => setWaOpen(false)}>
                {t("close")}
              </WaCancel>
              <WaSend type="button" onClick={submitWaVoucher}>
                <MessageCircle size={15} />
                {t("wa_send")}
              </WaSend>
            </WaActions>
          </WaCard>
        </QrBackdrop>
      ) : null}
      {confirmState ? (
        <QrBackdrop onClick={() => setConfirmState(null)}>
          <WaCard onClick={(event) => event.stopPropagation()}>
            <WaTitle>{confirmState.title}</WaTitle>
            <ConfirmBody>{confirmState.body}</ConfirmBody>
            {confirmState.note ? (
              <ConfirmNote>{confirmState.note}</ConfirmNote>
            ) : null}
            <WaActions>
              <WaCancel type="button" onClick={() => setConfirmState(null)}>
                {t("confirm_cancel")}
              </WaCancel>
              <ConfirmOk type="button" onClick={runConfirm}>
                <Check size={15} />
                {confirmState.confirmLabel}
              </ConfirmOk>
            </WaActions>
          </WaCard>
        </QrBackdrop>
      ) : null}
      {roomMove ? (
        <RoomMoveModal
          mode={roomMove.mode}
          bookingId={data.booking.id}
          changeLineId={roomMove.line.id}
          anchorLineId={lineId}
          currentCode={roomMove.line.room_code}
          onChanged={applyRoomMove}
          onClose={() => setRoomMove(null)}
        />
      ) : null}
      {emailOpen && data?.guest ? (
        <QrBackdrop onClick={() => setEmailOpen(false)}>
          <WaCard onClick={(event) => event.stopPropagation()}>
            <WaTitle>{t("email_edit")}</WaTitle>
            <WaDesc>{t("email_edit_desc")}</WaDesc>
            <WaInput
              type="email"
              inputMode="email"
              autoFocus
              style={{ width: "100%" }}
              placeholder="nama@email.com"
              value={emailValue}
              onChange={(event) => setEmailValue(event.target.value)}
            />
            {emailError ? <WaErr>{emailError}</WaErr> : null}
            <WaActions>
              <WaCancel type="button" onClick={() => setEmailOpen(false)}>
                {t("confirm_cancel")}
              </WaCancel>
              <WaSend type="button" onClick={submitEmail}>
                <Check size={15} />
                {t("email_save")}
              </WaSend>
            </WaActions>
          </WaCard>
        </QrBackdrop>
      ) : null}
      {phoneOpen && data?.guest ? (
        <QrBackdrop onClick={() => setPhoneOpen(false)}>
          <WaCard onClick={(event) => event.stopPropagation()}>
            <WaTitle>{t("phone_edit")}</WaTitle>
            <WaDesc>{t("phone_edit_desc")}</WaDesc>
            <PhoneField value={phoneValue} onChange={setPhoneValue} autoFocus />
            <WaHint>{t("wa_number_hint")}</WaHint>
            {phoneError ? <WaErr>{phoneError}</WaErr> : null}
            <WaActions>
              <WaCancel type="button" onClick={() => setPhoneOpen(false)}>
                {t("confirm_cancel")}
              </WaCancel>
              <WaSend type="button" onClick={submitPhone}>
                <Check size={15} />
                {t("phone_save")}
              </WaSend>
            </WaActions>
          </WaCard>
        </QrBackdrop>
      ) : null}
    </>
  );
};

export default BookingDrawer;
